import React from 'react';
import { observer } from 'mobx-react-lite';

import { ContextualDrawer } from '../../../app/components/contextual-drawer';
import { DateSelector } from '../../../components/date-selector/date-selector';
import { StepStatusSelector } from '../../process/components/step-status-selector';
import { type PlainDate } from '../../../core/services/plain-date';

import type { Step } from '../../../app/entities/step';

import styles from './step.drawer.module.scss';
interface StepContextualDrawerProps {
  step: Step;
  canValidate?: boolean;
}

export const StepDrawer: React.FC<React.PropsWithChildren<StepContextualDrawerProps>> = observer(
  ({ step, canValidate = true, children }) => {
    const handleUpdateTimeFrame = (values: {
      dueDate?: PlainDate | null;
      startDate?: PlainDate | null;
    }) => {
      step.update(values);
    };

    return (
      <ContextualDrawer>
        <Container>
          <Card>
            <Title>{step.name}</Title>

            <Properties>
              {canValidate && (
                <Property>
                  <Label>Status</Label>
                  <Action>
                    <StepStatusSelector stepId={step._id} />
                  </Action>
                </Property>
              )}

              <Property>
                <Label>Start date</Label>
                <Action>
                  <DateSelector
                    date={step.startDate}
                    max={step.dueDate}
                    field={'startDate'}
                    onUpdate={(startDate) => handleUpdateTimeFrame({ startDate })}
                  />
                </Action>
              </Property>

              <Property>
                <Label>Due date</Label>
                <Action>
                  <DateSelector
                    date={step.dueDate}
                    min={step.startDate}
                    field={'dueDate'}
                    onUpdate={(dueDate) => handleUpdateTimeFrame({ dueDate })}
                  />
                </Action>
              </Property>
            </Properties>
          </Card>

          {children}
        </Container>
      </ContextualDrawer>
    );
  },
);

const Container = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['container']} ${className}` : styles['container']}
    {...props}
  />
);

const Card = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['card']} ${className}` : styles['card']} {...props} />
);

const Title = ({ className, ...props }: React.ComponentPropsWithoutRef<'h1'>) => (
  <h1 className={className ? `${styles['title']} ${className}` : styles['title']} {...props} />
);

export const Properties = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['properties']} ${className}` : styles['properties']}
    {...props}
  />
);

export const Property = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['property']} ${className}` : styles['property']}
    {...props}
  />
);

export const Label = ({ className, ...props }: React.ComponentPropsWithoutRef<'label'>) => (
  <label className={className ? `${styles['label']} ${className}` : styles['label']} {...props} />
);

export const Action = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['action']} ${className}` : styles['action']} {...props} />
);
